import { IInsights } from '../../Contracts/IInsights';
import { GlobalEventBus } from '../GlobalEventBus';
import ROUTE_NAMES from '../../Widgets/ModalWidget/Routes/constants';
import { WIDGET_STATE_EVENTS } from '../../Events/Widget/State';
import { Widget } from '../../Contracts/WidgetTypes';
import { SESSION_EVENTS } from '../../Events/Session';
import { WidgetLocationRaw } from '../../Composables/WidgetNavigation/contracts';
import { AB_TESTER_EVENTS } from '../../Events/ABTester/Events';
import { AFFILIATE_LINK_EVENTS } from '../../Events/AffiliateLink/Events';
import { CHECKOUT_EVENTS } from '../../Events/Checkout/events';

export type MappedInsightEvent = {
    event: string;
    properties: Record<string, unknown>;
};

/**
 * Verbatim copy of `PosthogAnalytics.mapNavigationChangedEvent`.
 * Kept separate (and parity-tested) so the existing instance stays untouched while both
 * PostHog instances are tracked in parallel. Once the migration is complete the old class can be
 * removed and this becomes the single implementation.
 *
 * @param details - Raw widget navigation location emitted on the event bus
 * @returns The PostHog event name and properties to capture
 */
export const mapNavigationChangedEvent = (
    details: WidgetLocationRaw
): MappedInsightEvent => {
    switch (details.name) {
        case ROUTE_NAMES.BUTTON_VIEW:
            return {
                event: 'element_view',
                properties: {
                    element: 'floating_button.button',
                    ...details.props,
                },
            };
        case ROUTE_NAMES.EXPERIENCE_LIST:
            return {
                event: 'page_view',
                properties: {
                    page_title: 'floating_widget.list_widget',
                    ...details.props,
                },
            };
        case ROUTE_NAMES.DATE_TIME_SELECTION:
            return {
                event: 'page_view',
                properties: {
                    page_title: 'floating_widget.experience_details',
                    ...details.props,
                },
            };
        default:
            return {
                event: 'page_view',
                properties: {
                    ...details.props,
                },
            };
    }
};

/**
 * Verbatim copy of `PosthogAnalytics.listen()`, targeting any `IInsights` sink.
 *
 * @param eventBus - The global event bus to subscribe to
 * @param sink - The insights implementation that receives the mapped calls
 * @returns void
 */
export const subscribeInsightsToEventBus = (
    eventBus: typeof GlobalEventBus,
    sink: IInsights
): void => {
    let sessionUserId: string;
    const shouldTrackWidgetEvents = (options: Widget['options']) =>
        options.collectTraces;
    eventBus.listen(SESSION_EVENTS.SESSION_CREATED, ({ sessionId }) => {
        sessionUserId = sessionId;
    });
    eventBus.listen(
        AFFILIATE_LINK_EVENTS.AFFILIATE_LINK_DETECTED,
        ({ affiliateLink }) => {
            sink.trackEvent('Assign affiliate link to user', {
                $set: { affiliateLink },
            });
        }
    );
    eventBus.listen(
        WIDGET_STATE_EVENTS.WIDGET_MOUNTED_WITH_SUPPLIER_ID,
        ({ supplierId }) => {
            sink.identify(sessionUserId, supplierId);
        }
    );
    eventBus.listen(WIDGET_STATE_EVENTS.WIDGET_MOUNTED, (event) => {
        if (!shouldTrackWidgetEvents(event.widget.options)) {
            return;
        }
        sink.trackEvent('widget_loaded', event.widget);
    });
    eventBus.listen(
        WIDGET_STATE_EVENTS.WIDGET_NAVIGATION_CHANGED,
        (eventData) => {
            if (!shouldTrackWidgetEvents(eventData.widget.options)) {
                return;
            }
            const { event, properties } = mapNavigationChangedEvent(eventData);
            sink.trackEvent(event, properties);
        }
    );
    eventBus.listen(AB_TESTER_EVENTS.ENROLLED_EXPERIMENT, (eventData) => {
        sink.trackEvent('experiment_enrollment', eventData);
    });
    eventBus.listen(CHECKOUT_EVENTS.MESSAGE_FROM_CHECKOUT, (data: unknown) => {
        if (!data || typeof data !== 'object') return;
        const { event, ...raw } = data as { event?: unknown } & Record<
            string,
            unknown
        >;
        if (typeof event !== 'string' || !event.trim()) return;
        sink.trackEvent(event.trim(), raw);
    });
};
